import axios from "axios";
import { z } from "zod";
import { urljoin } from "../../baseConnector";
export class DigikeyAuthenticator {
    baseUrl;
    authParams;
    credentialManager;
    http;
    constructor({ credentialManager, baseUrl }) {
        this.credentialManager = credentialManager;
        this.baseUrl = baseUrl ?? "https://api.digikey.com/v1";
        this.http = axios.create();
    }
    async authenticate() {
        const token = await this.getAuthToken();
        if (!token) {
            throw new Error(`Missing initial credentials for Digi-Key API: sign in at ${await this.getAuthUrl()}`);
        }
        const authParams = await this.getAuthParams();
        let authenticatedToken;
        if (token.isValid) {
            authenticatedToken = token;
        }
        else if (token.isRefreshable) {
            try {
                authenticatedToken = await this.refreshAccessToken();
            }
            catch (e) {
                throw new Error(`Failed to refresh Digi-Key API access token: ${e}. Re-authenticate by signing in at ${await this.getAuthUrl()}`);
            }
        }
        else {
            throw new Error(`Expired Digi-Key API refresh token: re-authenticate by signing in at ${await this.getAuthUrl()}`);
        }
        return {
            token: authenticatedToken,
            params: authParams,
        };
    }
    async getAuthUrl() {
        const authParams = await this.getAuthParams();
        const params = new URLSearchParams({
            response_type: "code",
            client_id: authParams.clientId,
            redirect_uri: authParams.redirectUri,
            state: "",
        });
        return urljoin(this.baseUrl, "oauth2/authorize") + "?" + params.toString();
    }
    async getAccessToken(code) {
        const authParams = await this.getAuthParams();
        const response = await this.http.post(urljoin(this.baseUrl, "oauth2/token"), new URLSearchParams({
            code,
            grant_type: "authorization_code",
            client_id: authParams.clientId,
            client_secret: authParams.clientSecret,
            redirect_uri: authParams.redirectUri,
        }), {
            headers: {
                "Content-Type": "application/x-www-form-urlencoded",
            },
        });
        return AuthTokenSchema.parse(response.data);
    }
    async refreshAccessToken() {
        const oldToken = await this.getAuthToken();
        if (!oldToken) {
            throw new Error("No auth token to refresh");
        }
        const unlockToken = await this.credentialManager.lockToken();
        const authParams = await this.getAuthParams();
        const timestamp = Date.now();
        const response = await this.http.post(urljoin(this.baseUrl, "oauth2/token"), new URLSearchParams({
            client_id: authParams.clientId,
            client_secret: authParams.clientSecret,
            refresh_token: oldToken.refreshToken,
            grant_type: "refresh_token",
        }), {
            headers: {
                "Content-Type": "application/x-www-form-urlencoded",
            },
        });
        const token = AuthTokenSchema.parse(response.data);
        await this.credentialManager.setToken({
            updated_at: timestamp,
            ...token,
        });
        await unlockToken();
        return new AuthToken(token);
    }
    async getAuthToken() {
        const token = await this.credentialManager.getToken();
        if (token) {
            return new AuthToken(token);
        }
        else {
            return null;
        }
    }
    async getAuthParams() {
        if (!this.authParams) {
            this.authParams = await this.credentialManager.getAuthParams();
        }
        return this.authParams;
    }
}
export const AuthTokenSchema = z.object({
    access_token: z.string(),
    refresh_token: z.string(),
    expires_in: z.number(),
    refresh_token_expires_in: z.number(),
    token_type: z.string(),
    updated_at: z.number().optional(),
});
class AuthToken {
    accessToken;
    refreshToken;
    tokenType;
    accessExpiresAt;
    refreshExpiresAt;
    constructor(params) {
        this.accessToken = params.access_token;
        this.refreshToken = params.refresh_token;
        this.tokenType = params.token_type;
        let baseTime;
        if (params.updated_at) {
            baseTime = params.updated_at;
        }
        else {
            baseTime = Date.now();
        }
        this.accessExpiresAt = new Date(baseTime + params.expires_in * 1000);
        this.refreshExpiresAt = new Date(baseTime + params.refresh_token_expires_in * 1000);
    }
    get isValid() {
        return this.accessExpiresAt > new Date();
    }
    get isRefreshable() {
        return this.refreshExpiresAt > new Date();
    }
}
//# sourceMappingURL=authenticator.js.map